import { ChevronRight, Send, Sparkles, User } from "lucide-react";
import { useState } from "react";
import { MOCK_AI_ANSWERS, MOCK_COMMUNITY, type MockAiAnswer } from "@/src/common/components/app-mock/mock-community";
import { MOCK_TOKENS, MockButton } from "@/src/common/components/app-mock/primitives";
import { useCommunityVariant } from "@/src/common/components/product-demo/community-variant-context";

function MiniBars({ answer }: { answer: MockAiAnswer }) {
	const { palette } = useCommunityVariant();
	if (!answer.bars) return null;
	const max = Math.max(...answer.bars.map((bar) => bar.value));
	return (
		<div className="mt-2 flex h-16 items-end gap-1.5 px-1" aria-hidden="true" data-mock="ai-chart">
			{answer.bars.map((bar) => (
				<div key={bar.label} className="flex flex-1 flex-col items-center gap-0.5">
					<span
						className="w-full rounded-t-sm"
						style={{ height: `${Math.max(6, Math.round((bar.value / max) * 52))}px`, background: palette.accent }}
					/>
					<span className="text-[9px] text-neutral-400">{bar.label}</span>
				</div>
			))}
		</div>
	);
}

function AssistantBubble({ answer }: { answer: MockAiAnswer }) {
	const { palette } = useCommunityVariant();
	return (
		<div className="flex gap-2" data-mock="ai-answer">
			<span
				className="inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-white"
				style={{ boxShadow: MOCK_TOKENS.surfaceShadow }}
				aria-hidden="true"
			>
				<Sparkles className="size-3" style={{ color: palette.accent }} />
			</span>
			<div className="min-w-0 flex-1 rounded-2xl bg-white px-3 py-2.5" style={{ boxShadow: MOCK_TOKENS.surfaceShadow }}>
				<p className="text-[11px] leading-relaxed text-neutral-800">{answer.answer}</p>
				<div className="mt-2 grid grid-cols-3 gap-1.5">
					{answer.metrics.map((metric) => (
						<div
							key={metric.label}
							className="rounded-lg px-2 py-1.5"
							style={{ background: MOCK_TOKENS.surfaceSecondary }}
							data-mock="metric-tile"
						>
							<p className="text-[9px] text-neutral-500">{metric.label}</p>
							<p className="text-sm font-bold text-neutral-900">{metric.value}</p>
							{metric.delta ? (
								<p className="text-[9px] font-medium" style={{ color: MOCK_TOKENS.success }}>
									{metric.delta}
								</p>
							) : null}
						</div>
					))}
				</div>
				<MiniBars answer={answer} />
				<div className="mt-2">
					<p className="flex items-center gap-1 text-[10px] font-semibold text-neutral-900">
						<Sparkles className="size-3" style={{ color: palette.accent }} />
						Insights
					</p>
					<ul className="mt-1 space-y-0.5">
						{answer.insights.map((insight) => (
							<li
								key={insight.text}
								className="rounded-md px-2 py-1 text-[10px] text-neutral-700"
								style={{ background: MOCK_TOKENS.surfaceSecondary }}
								data-mock="insight-item"
							>
								<span className="font-medium" style={{ color: palette.accent }}>
									{insight.type}
								</span>
								{" · "}
								{insight.text}
							</li>
						))}
					</ul>
				</div>
				<div className="mt-2 rounded-md px-2 py-1 text-[10px] text-neutral-600" style={{ background: MOCK_TOKENS.surfaceSecondary }}>
					<span className="inline-flex items-center gap-1 font-medium">
						<ChevronRight className="size-3" />
						Generated SQL
					</span>
				</div>
				<p className="mt-1.5 text-[9px] text-neutral-500">{answer.meta}</p>
			</div>
		</div>
	);
}

interface WebAiInsightsScreenProps {
	interactive?: boolean;
	initialAnswerId?: string;
	promptChips?: boolean;
	question?: string;
	showAnswer?: boolean;
	answerClassName?: string;
}

export function WebAiInsightsScreen({
	interactive,
	initialAnswerId = "growth",
	promptChips = true,
	question,
	showAnswer = true,
	answerClassName = "",
}: WebAiInsightsScreenProps) {
	const { palette } = useCommunityVariant();
	const [answerId, setAnswerId] = useState(initialAnswerId);
	const answer = MOCK_AI_ANSWERS.find((entry) => entry.id === answerId) ?? MOCK_AI_ANSWERS[0];
	if (!answer) return null;

	return (
		<div className="flex h-full min-h-0 flex-col" data-mock="ai-insights">
			<header className="flex items-center justify-between border-b px-4 py-2.5" style={{ borderColor: MOCK_TOKENS.border }}>
				<span className="flex items-center gap-1.5">
					<Sparkles className="size-3.5" style={{ color: palette.accent }} />
					<span className="text-xs font-semibold text-neutral-900">AI Insights</span>
				</span>
				<span className="text-[10px] text-neutral-500">3 requests today</span>
			</header>
			<div className="mock-scroll min-h-0 flex-1 overflow-y-auto px-4 py-3">
				<div className="mx-auto max-w-2xl space-y-3">
					<div className="flex flex-row-reverse gap-2" data-mock="ai-question">
						<span
							className="inline-flex size-6 shrink-0 items-center justify-center rounded-full"
							style={{ background: palette.accentSoft }}
							aria-hidden="true"
						>
							<User className="size-3" style={{ color: palette.accent }} />
						</span>
						<span className="rounded-2xl px-3 py-2 text-[11px] text-neutral-900" style={{ background: palette.accentSoft }}>
							{question ?? answer.question}
						</span>
					</div>
					{showAnswer ? (
						<div className={answerClassName}>
							<AssistantBubble answer={answer} />
						</div>
					) : null}
				</div>
			</div>
			<div className="border-t px-4 py-2.5" style={{ borderColor: MOCK_TOKENS.border }}>
				<div className="mx-auto max-w-2xl space-y-2">
					{promptChips ? (
						<div className="flex flex-wrap gap-1" data-mock="ai-prompts">
							{MOCK_AI_ANSWERS.map((entry) => {
								const isActive = entry.id === answer.id;
								const classes = "rounded-full border px-2 py-0.5 text-[10px] font-medium";
								const style = isActive
									? { borderColor: palette.accent, color: palette.accentInk, background: palette.accentSoft }
									: { borderColor: MOCK_TOKENS.border, color: "#525252" };
								return interactive ? (
									<button
										key={entry.id}
										type="button"
										onClick={() => setAnswerId(entry.id)}
										aria-pressed={isActive}
										className={classes}
										style={style}
									>
										{entry.question}
									</button>
								) : (
									<span key={entry.id} className={classes} style={style} aria-hidden="true">
										{entry.question}
									</span>
								);
							})}
						</div>
					) : null}
					<div
						className="flex items-end gap-2 rounded-xl border bg-white p-2"
						style={{ borderColor: MOCK_TOKENS.border, boxShadow: MOCK_TOKENS.surfaceShadow }}
					>
						<span className="min-h-8 flex-1 text-[11px] text-neutral-400">Ask a question about your community data...</span>
						<MockButton size="xs" icon={Send}>
							Send
						</MockButton>
					</div>
					<p className="text-center text-[9px] text-neutral-400">Owner and admin only. Powered by GPT, scoped to {MOCK_COMMUNITY.name}.</p>
				</div>
			</div>
		</div>
	);
}
